import util from '../util';
import Widget from './Widget';

export default class GiftGoalWidget extends Widget {
	constructor(opts) {
		super(opts);
	}

	applySettings(settings) {
		super.applySettings(settings);
		// Patch in currency_code from userData (loaded before settings in loadConfig).
		if(this.userData?.currency_code)
			this.settings.currency_code = this.userData.currency_code;
		this.setGoal(parseFloat(this.settings.goal || 0) || 0);
		this.setAmount(parseFloat(this.settings.amount || 0) || 0);
		this.setTitle(this.settings.title);
	}

	applyUserData(data) {
		super.applyUserData(data);
		// On reconnect, patch currency_code into already-applied settings.
		if(this.settings && data?.currency_code) {
			this.settings.currency_code = data.currency_code;
			this.computeSize();
		}
	}


	_build() {
		this.container.innerHTML = `
			<div class="progressBar">
				<div class="progressAmount"></div>
				<div class="title"></div>
				<div class="progressText"></div>
				<div class="goal"></div>
			</div>
		`;
	}

	_generateCSS() {
		const css = `
			#${this.container.id} {
				height: 100%;
				display: flex;
				flex-direction: column;
				justify-content: ${{ top: 'flex-start', middle: 'center', bottom: 'flex-end' }[this.settings.verticalAlign] || 'center'};
				font-family: Arial, sans-serif;
				color: ${this.settings.textColor || '#ffffff'};
				${this.settings.fontFamily ? `font-family: ${this.settings.fontFamily};` : ''}
				font-size: ${this.settings.fontSize || 40}px;
				${this.settings.letterSpacing > 0 ? `letter-spacing: ${this.settings.letterSpacing}px;` : ''}
				${this.settings.boldText ? `font-weight: bold;` : ''}
				${this.settings.italicText ? `font-style: italic;` : ''}
				${this.settings.underlineText || this.settings.strikethroughText ? `text-decoration: ${this.settings.underlineText ? 'underline' : ''} ${this.settings.strikethroughText ? 'line-through' : ''};` : ''}
				${this.settings.uppercaseText ? `text-transform: uppercase;` : ''}
			}
			#${this.container.id} > .progressBar {
				z-index: 0;
				position: relative;
				display: flex;
				justify-content: space-between;
				background-color: ${this.settings.backColor || '#000000'};
				background-clip: padding-box;
				${this.settings.borderRadius > 0 ? `border-radius: ${this.settings.borderRadius}px;` : ''}
				overflow: hidden;
				${this.settings.borderWidth > 0 && this.settings.borderColor ? `border: ${this.settings.borderWidth}px solid ${this.settings.borderColor};` : ''}
				${this.settings.padding > 0 ? `padding: ${this.settings.padding}px;` : ''}
			}
			#${this.container.id} > .progressBar > .progressAmount {
				z-index: 1;
				position: absolute;
				left: 0;
				top: 0;
				bottom: 0;
				width: 0%;
				background-color: ${this.settings.barColor || '#007fff'};
				${this.settings.stripes ? `
				background-image: -webkit-linear-gradient(-60deg, rgba(255,255,255,.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.15) 50%, rgba(255,255,255,.15) 75%, transparent 75%, transparent);
				background-image: -o-linear-gradient(-60deg, rgba(255,255,255,.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.15) 50%, rgba(255,255,255,.15) 75%, transparent 75%, transparent);
				background-image: linear-gradient(-60deg, rgba(255,255,255,.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.15) 50%, rgba(255,255,255,.15) 75%, transparent 75%, transparent);
				background-size: 46px 80px;
				-webkit-animation: progressBarStripes 2s linear infinite;
				-o-animation: progressBarStripes 2s linear infinite;
				animation: progressBarStripes 2s linear infinite;
			` : ''}
				-webkit-transition: width .5s ease-in-out;
				-moz-transition: width .5s ease-in-out;
				-o-transition: width .5s ease-in-out;
				transition: width .5s ease-in-out;
			}
			@-webkit-keyframes progressBarStripes {
				from { background-position: 46px 0; }
				to   { background-position: 0 0; }
			}
			@keyframes progressBarStripes {
				from { background-position: 46px 0; }
				to   { background-position: 0 0; }
			}
			#${this.container.id} > .progressBar > .progressText {
				z-index: 3;
				${this.settings.blurRadius > 0 || this.settings.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius, this.settings.blurRadius, this.settings.shadowColor)};` : ''}
			}
			#${this.container.id} > .progressBar > .title {
				white-space: pre;
				z-index: 2;
				${this.settings.blurRadius > 0 || this.settings.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius, this.settings.blurRadius, this.settings.shadowColor)};` : ''}
			}
			#${this.container.id} > .progressBar > .goal {
				z-index: 4;
				${this.settings.blurRadius > 0 || this.settings.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius, this.settings.blurRadius, this.settings.shadowColor)};` : ''}
			}
			${this.settings.extraCSS || ''}
		`;
		this._setCSS('progressBarWidgetStyle', css);
		this._updateFontLink();
	}

	setGoal(goal) {
		this.goal = goal;
		this.computeSize();
	}

	setAmount(amount) {
		this.amount = amount;
		this.computeSize();
	}

	addAmount(amount) {
		this.setAmount((this.amount || 0) + amount);
	}

	computeSize() {
		if(!this.settings) return;
		const progressAmountEl = this.container.querySelector('.progressAmount');
		if(!progressAmountEl) return;
		const pct = Math.round((this.goal ? (this.amount || 0) / this.goal : 0) * 100);
		progressAmountEl.style.width = `${pct}%`;
		const currency = this.settings.currency_code || 'USD';
		let progressText = this.settings.hideCurrency ? (this.amount || 0) : util.generateValueString(this.amount || 0, currency);
		let goalString = this.settings.hideCurrency ? (this.goal || 0) : util.generateValueString(this.goal || 0, currency);
		let goalText = '';
		if(!this.settings.hidePercentage) {
			if(this.settings.centerGoal)
				goalText += `${pct}%`;
			else
				progressText += ` (${pct}%)`;
		}
		if(this.settings.centerGoal) progressText += ` / ${goalString}`;
		else goalText += goalString;
		if(this.settings.appendToGoal)
			goalText += this.settings.appendToGoal;
		this.container.querySelector('.progressText').innerText = progressText;
		this.container.querySelector('.goal').innerText = goalText;
	}

	setTitle(subtitle) {
		this.subtitle = subtitle;
		const titleEl = this.container.querySelector('.title');
		if(titleEl) titleEl.innerText = subtitle || '';
	}
}
